import { useRef, type ReactNode } from 'react';
import { Trash2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { ADMIN_COLORS } from '@/features/admin/utils/adminAnimations';
import { useRTL } from '@/hooks/useRTL';
import { useMagnetic } from '@/hooks/useMagnetic';
import { useRipple } from '@/components/ui/Ripple';

/** A single mushroom-style floating action button */
function MushroomButton({
  onClick,
  disabled,
  ariaLabel,
  gradient,
  icon,
}: {
  onClick: () => void;
  disabled?: boolean;
  ariaLabel: string;
  gradient: string;
  icon: ReactNode;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const { x, y, onMouseMove, onMouseLeave } = useMagnetic(ref, { strength: 6, radius: 60 });
  useRipple(ref);

  return (
    <motion.button
      ref={ref}
      whileTap={{ scale: 0.82, y: 4 }}
      whileHover={{ scale: 1.08 }}
      onClick={onClick}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      disabled={disabled}
      className="relative flex items-center justify-center rounded-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
      style={{
        x,
        y,
        width: '62px',
        height: '62px',
        background: gradient,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: `1px solid ${ADMIN_COLORS.border}`,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
      aria-label={ariaLabel}
    >
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          top: '6px',
          left: '14px',
          width: '18px',
          height: '10px',
          background: 'rgba(255,255,255,0.45)',
          borderRadius: '50%',
        }}
      />
      {icon}
    </motion.button>
  );
}

const DELETE_GRADIENT =
  'radial-gradient(ellipse at 50% 30%, rgba(255,140,140,0.95) 0%, rgba(255,80,80,0.85) 60%, rgba(200,50,50,0.8) 100%)';

interface BulkActionBarProps {
  count: number;
  onDelete: () => void;
  onClear: () => void;
  isDeleting?: boolean;
  /** Extra mushroom buttons between delete and dismiss */
  extraActions?: ReactNode;
}

export function BulkActionBar({
  count,
  onDelete,
  onClear,
  isDeleting,
  extraActions,
}: BulkActionBarProps) {
  const isRTL = useRTL();

  return (
    <AnimatePresence>
      {count > 0 && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring' as const, stiffness: 300, damping: 28 }}
          className="flex items-center gap-3 py-3"
          style={{
            justifyContent: isRTL ? 'flex-end' : 'flex-start',
          }}
        >
          {/* Count badge */}
          <div
            className="w-10 h-10 rounded-full border border-white/30 bg-white flex items-center justify-center font-bold text-sm"
            style={{ boxShadow: ADMIN_COLORS.shadow }}
          >
            {count}
          </div>

          {/* Delete mushroom */}
          <MushroomButton
            onClick={onDelete}
            disabled={isDeleting}
            ariaLabel="Delete"
            gradient={DELETE_GRADIENT}
            icon={<Trash2 className="w-7 h-7 text-slate-700 relative z-[1]" />}
          />

          {extraActions}

          {/* Dismiss */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={onClear}
            className="w-10 h-10 rounded-full border border-white/30 bg-white hover:bg-white/20 flex items-center justify-center cursor-pointer"
            style={{ boxShadow: ADMIN_COLORS.shadow }}
          >
            <X className="w-4 h-4" />
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Re-export for pages that need extra mushroom-style buttons */
BulkActionBar.MushroomButton = MushroomButton;
export { MushroomButton };
